import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  MdNotificationsActive,
  MdWarning,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdDateRange,
  MdPerson,
  MdContentCut,
  MdCheckCircle,
  MdErrorOutline,
} from "react-icons/md";
import { useAuth } from "context/AuthContext";
// Kendi api servis yolunu buraya göre ayarlarsın dostum
import { followUpAPI } from "services/api";

// --- SKELETON LOADER ---
const ListSkeleton = () => (
  <div className="mt-6 flex animate-pulse flex-col gap-4">
    <div className="h-12 w-full rounded-xl bg-gray-200 dark:bg-navy-700" />
    <div className="grid grid-cols-1 gap-4 md:hidden">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="h-28 w-full rounded-[20px] bg-gray-100 dark:bg-navy-900/50"
        />
      ))}
    </div>
    <div className="hidden flex-col gap-3 md:flex">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="h-16 w-full rounded-xl bg-gray-100 dark:bg-navy-900/50"
        />
      ))}
    </div>
  </div>
);

const PAGE_SIZE = 10;

const VaultFollowUp = () => {
  const { logout } = useAuth();
  const handleAuthError = useCallback(() => logout(), [logout]);

  // --- STATELER ---
  const [dueCustomers, setDueCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [actionLoading, setActionLoading] = useState({}); // Hangi butona basıldığını takip etmek için
  const [currentPage, setCurrentPage] = useState(1);

  // --- VERİ ÇEKME ---
  const fetchDueCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await followUpAPI.getDueCustomers();
      if (response.success) {
        setDueCustomers(response.data || []);
      } else {
        throw new Error(response.message || "Veriler alınamadı.");
      }
    } catch (err) {
      console.error("Follow-up veri yükleme hatası:", err);
      if (
        String(err.message).includes("401") ||
        String(err.message).includes("Unauthorized")
      ) {
        handleAuthError();
        return;
      }
      setError(err.message || "Müşteri listesi yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => {
    let mounted = true;
    if (mounted) fetchDueCustomers();
    return () => {
      mounted = false;
    };
  }, [fetchDueCustomers]);

  // --- BİLDİRİM GÖNDERME İŞLEMİ ---
  const handleSendNotification = async (appointmentId) => {
    setActionLoading((prev) => ({ ...prev, [appointmentId]: true }));
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await followUpAPI.sendFollowUpNotification(
        appointmentId
      );
      if (response.success) {
        setSuccessMessage("Bildirim başarıyla gönderildi!");
        // Başarılı olanı listeden çıkar (veya statusunu güncelle)
        setDueCustomers((prev) =>
          prev.filter((appt) => appt.id !== appointmentId)
        );

        // Eğer sayfadaki son elemanı sildiysek bir önceki sayfaya geç
        if (pagedData.length === 1 && currentPage > 1) {
          setCurrentPage((prev) => prev - 1);
        }
      } else {
        throw new Error(response.message || "Bildirim gönderilemedi.");
      }
    } catch (err) {
      setError(err.message || "Bildirim gönderilirken bir hata oluştu.");
    } finally {
      setActionLoading((prev) => ({ ...prev, [appointmentId]: false }));

      // 3 saniye sonra başarı/hata mesajını temizle
      setTimeout(() => {
        setSuccessMessage(null);
        setError(null);
      }, 3000);
    }
  };

  // --- SAYFALAMA HESAPLAMALARI ---
  const totalPages = Math.ceil(dueCustomers.length / PAGE_SIZE);

  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return dueCustomers.slice(start, start + PAGE_SIZE);
  }, [dueCustomers, currentPage]);

  const handlePageChange = (diff) => {
    setCurrentPage((prev) => prev + diff);
  };

  // Tarih formatlayıcı
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("tr-TR", options);
  };

  return (
    <div className="animate-fadeIn mt-5 flex w-full flex-col gap-6 pb-20">
      {/* 1. ÜST BAŞLIK */}
      <div className="flex flex-col items-center justify-between gap-6 rounded-[20px] border border-gray-100 bg-white p-6 shadow-3xl shadow-shadow-500 dark:border-white/10 dark:bg-navy-800 dark:shadow-none md:flex-row">
        <div className="flex w-full items-center gap-4 md:w-auto">
          <div className="flex h-14 w-14 flex-none items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400">
            <MdNotificationsActive className="text-3xl" />
          </div>
          <div>
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">
              Müşteri Takip & Hatırlatma (Beta Sürümüdür)
            </h4>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Son ziyaretinin üzerinden 21 gün geçen müşteriler.
            </p>
          </div>
        </div>

        <div className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-50 px-4 py-2 font-bold text-brand-500 dark:bg-brand-400/10 dark:text-brand-400 md:w-auto">
          Toplam Bekleyen: {dueCustomers.length}
        </div>
      </div>

      {/* UYARI VE BAŞARI MESAJLARI */}
      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-600 transition-all dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400">
          <MdErrorOutline className="flex-none text-xl" /> {error}
        </div>
      )}

      {successMessage && (
        <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 p-4 text-sm font-bold text-green-600 transition-all dark:border-green-500/20 dark:bg-green-500/10 dark:text-green-400">
          <MdCheckCircle className="flex-none text-xl" /> {successMessage}
        </div>
      )}

      {/* 2. LİSTE ALANI */}
      <div className="flex min-h-[400px] flex-col rounded-[20px] border border-gray-100 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
        {loading ? (
          <ListSkeleton />
        ) : dueCustomers.length === 0 ? (
          <div className="m-auto flex flex-col items-center justify-center py-20 opacity-50">
            <MdNotificationsActive className="mb-4 text-6xl text-gray-300 dark:text-gray-600" />
            <h5 className="text-xl font-bold text-navy-700 dark:text-white">
              Hatırlatma Bekleyen Yok
            </h5>
            <p className="text-sm text-gray-500">
              21 günü geçen tüm müşterilerle ilgilenilmiş!
            </p>
          </div>
        ) : (
          <>
            {/* MASAÜSTÜ TABLO GÖRÜNÜMÜ */}
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-100 text-[11px] font-bold uppercase tracking-widest text-gray-400 dark:border-white/10">
                    <th className="py-4 pl-2">Müşteri Bilgisi</th>
                    <th className="py-4">Son Alınan Hizmet</th>
                    <th className="py-4">Son İşlem Tarihi</th>
                    <th className="py-4 pr-2 text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedData.map((appt) => (
                    <tr
                      key={appt.id}
                      className="border-b border-gray-50 transition-colors hover:bg-gray-50/50 dark:border-white/5 dark:hover:bg-white/5"
                    >
                      <td className="py-4 pl-2">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 font-bold text-navy-700 dark:bg-navy-700 dark:text-white">
                            {appt.customer?.name?.charAt(0)}
                            {appt.customer?.surname?.charAt(0)}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-navy-700 dark:text-white">
                              {appt.customer?.name} {appt.customer?.surname}
                            </p>
                            <p className="text-xs text-gray-500">
                              {appt.customer?.phone || "Telefon Kayıtlı Değil"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4">
                        <div className="flex items-center gap-2">
                          <MdContentCut className="text-gray-400" />
                          <span className="text-sm font-bold text-navy-700 dark:text-white">
                            {appt.service?.name || "Belirtilmemiş"}
                          </span>
                        </div>
                      </td>
                      <td className="py-4">
                        <div className="flex items-center gap-2">
                          <MdDateRange className="text-gray-400" />
                          <span className="text-sm font-bold text-navy-700 dark:text-white">
                            {formatDate(appt.appointment_date)}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 pr-2 text-right">
                        <button
                          onClick={() => handleSendNotification(appt.id)}
                          disabled={actionLoading[appt.id]}
                          className={`ml-auto flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition-all
                                                        ${
                                                          actionLoading[appt.id]
                                                            ? "cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-navy-700 dark:text-gray-400"
                                                            : "bg-brand-500 text-white shadow-md shadow-brand-500/20 hover:bg-brand-600 active:scale-95"
                                                        }`}
                        >
                          {actionLoading[appt.id] ? (
                            <>
                              <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-gray-400" />
                              Gönderiliyor...
                            </>
                          ) : (
                            "Bildirim Gönder"
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* MOBİL KART GÖRÜNÜMÜ (OVERFLOW YOK, DİKEY YERLEŞİM) */}
            <div className="mt-2 grid grid-cols-1 gap-4 md:hidden">
              {pagedData.map((appt) => (
                <div
                  key={appt.id}
                  className="relative flex flex-col rounded-[16px] border border-gray-100 bg-gray-50/50 p-4 dark:border-white/5 dark:bg-navy-900/30"
                >
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-lg font-bold text-brand-500 dark:bg-brand-400/10 dark:text-brand-400">
                      {appt.customer?.name?.charAt(0)}
                      {appt.customer?.surname?.charAt(0)}
                    </div>
                    <div>
                      <h6 className="text-base font-bold text-navy-700 dark:text-white">
                        {appt.customer?.name} {appt.customer?.surname}
                      </h6>
                      <p className="text-xs font-medium text-gray-500">
                        {appt.customer?.phone || "Telefon Yok"}
                      </p>
                    </div>
                  </div>

                  <div className="mb-4 grid grid-cols-2 gap-2 rounded-xl border border-gray-100 bg-white p-3 dark:border-white/5 dark:bg-navy-800">
                    <div>
                      <span className="mb-1 block text-[10px] font-bold uppercase text-gray-400">
                        Hizmet
                      </span>
                      <span className="line-clamp-1 text-xs font-bold text-navy-700 dark:text-white">
                        {appt.service?.name || "-"}
                      </span>
                    </div>
                    <div>
                      <span className="mb-1 block text-[10px] font-bold uppercase text-gray-400">
                        Tarih
                      </span>
                      <span className="text-xs font-bold text-navy-700 dark:text-white">
                        {formatDate(appt.appointment_date)}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleSendNotification(appt.id)}
                    disabled={actionLoading[appt.id]}
                    className={`flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition-all
                                            ${
                                              actionLoading[appt.id]
                                                ? "cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-navy-700 dark:text-gray-400"
                                                : "bg-brand-500 text-white shadow-md shadow-brand-500/20 hover:bg-brand-600 active:scale-95"
                                            }`}
                  >
                    {actionLoading[appt.id] ? (
                      <>
                        <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-gray-400" />
                        Gönderiliyor...
                      </>
                    ) : (
                      "Bildirim Gönder"
                    )}
                  </button>
                </div>
              ))}
            </div>

            {/* SAYFALAMA */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4 dark:border-white/10">
                <button
                  onClick={() => handlePageChange(-1)}
                  disabled={currentPage === 1}
                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 transition-all hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white dark:border-white/10 dark:bg-navy-800 dark:text-gray-400 dark:hover:bg-navy-700"
                >
                  <MdKeyboardArrowLeft className="text-xl" />
                </button>

                <span className="rounded-xl bg-gray-50 px-4 py-2 text-sm font-bold text-navy-700 dark:bg-navy-900 dark:text-white">
                  Sayfa {currentPage} / {totalPages}
                </span>

                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === totalPages}
                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 transition-all hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-white dark:border-white/10 dark:bg-navy-800 dark:text-gray-400 dark:hover:bg-navy-700"
                >
                  <MdKeyboardArrowRight className="text-xl" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VaultFollowUp;
